import { create } from "zustand";
import { devtools } from "zustand/middleware";

/**
 * Unread-chat and PubNub state.
 *
 * This must be a single store shared by every app in the bundle: oem's
 * MessageBox context (PubNub) writes the unread counts, and the customer
 * portal's sidebar reads them for its unread badges. Two instances would mean
 * the portal's badges were permanently zero.
 */

type ChatState = {
  pubnub: Record<string, any>;
  imagePreview: {
    isOpen: boolean;
    openedImage: string;
  };
  unreadMessages: {
    channels: any[];
    channelTimetokens: any[];
    messageRead: boolean;
  };
  unreadChannelMessages: {
    channels: Record<string, any>;
  };
  allMembershipChannels: Record<string, any>;
  /**
   * Connection id → the chat channels belonging to it, gathered from that
   * connection's channel groups. Empty until `useChatChannelGroupChannels` has
   * fetched them.
   *
   * This is what scopes a portal's unread badge to one connection. A chat
   * channel is named after the company that RAISED the ticket, so a rerouted
   * ticket — answered by a different company — carries a name that points at
   * the wrong portal; the channel group, which the backend reconciles on every
   * reroute toggle, is the only trustworthy statement of which connection a
   * conversation belongs to. See `usePortalUnreadCounts`.
   */
  chatChannelsByConnection: Record<string, string[]>;
  setChatOptions: (options: Partial<Pick<ChatState, "pubnub">>) => void;
  setImagePreview: (state: Partial<ChatState["imagePreview"]>) => void;
  setUnreadMessages: (state: Partial<ChatState["unreadMessages"]>) => void;
  setUnreadChannelMessages: (
    state: Partial<ChatState["unreadChannelMessages"]>,
  ) => void;
  /**
   * Merge per-channel counts into the map. Prefer this over
   * `setUnreadChannelMessages` everywhere except the full membership scan: the
   * setter replaces `channels` wholesale, so a caller that spreads a snapshot
   * taken before an await reverts every write that landed in between (a live
   * recount undoing a mark-as-read, a mark-as-read undoing an increment).
   */
  mergeUnreadChannelMessages: (channels: Record<string, any>) => void;
  /**
   * Track channels not in the map yet, at 0. Channels already known keep their
   * count — seeding must never overwrite a real unread count with zero.
   */
  seedUnreadChannels: (channelIds: string[]) => void;
  /**
   * Set a channel the map already tracks to 0 — what reading a chat does.
   * Never adds one: the map is seeded from memberships, and a chat the user
   * opened without being a member of it (an OEM owner reading a ticket a portal
   * has taken over) must not start counting their unread.
   */
  resetUnreadChannel: (channelId: string) => void;
  /**
   * +1 on a channel the map already tracks. Unknown channels are ignored — the
   * map is seeded from memberships, and a channel absent from it is one this
   * session has no membership on.
   */
  incrementUnreadChannel: (channelId: string) => void;
  /** Drop channels the user no longer has a membership on. */
  removeUnreadChannels: (channelIds: string[]) => void;
  setAllMembershipChannels: (channels: Record<string, any>) => void;
  /**
   * Replace the whole connection→channels map. A replace rather than a merge:
   * the fetcher always reads every group the credentials name, and a channel a
   * connection has LOST (de-escalation, reroute switched off) must go here
   * too, or the badge keeps counting a conversation the portal no longer owns.
   * The fetcher carries forward the previous entry for any group whose read
   * failed, so a partial failure never silently empties a connection.
   */
  setChatChannelsByConnection: (byConnection: Record<string, string[]>) => void;
};

export const useChatStore = create<ChatState>()(
  devtools(
    (set) => ({
      pubnub: {},
      imagePreview: {
        isOpen: false,
        openedImage: "",
      },
      unreadMessages: {
        channels: [],
        channelTimetokens: [],
        messageRead: false,
      },
      unreadChannelMessages: {
        channels: {},
      },
      allMembershipChannels: {},
      chatChannelsByConnection: {},
      setChatOptions: (options) =>
        set((s) => ({ pubnub: { ...s.pubnub, ...options.pubnub } })),
      setImagePreview: (state) =>
        set((s) => ({ imagePreview: { ...s.imagePreview, ...state } })),
      setUnreadMessages: (state) =>
        set((s) => ({ unreadMessages: { ...s.unreadMessages, ...state } })),
      setUnreadChannelMessages: (state) =>
        set((s) => ({
          unreadChannelMessages: { ...s.unreadChannelMessages, ...state },
        })),
      mergeUnreadChannelMessages: (channels) =>
        set((s) => ({
          unreadChannelMessages: {
            ...s.unreadChannelMessages,
            channels: { ...s.unreadChannelMessages.channels, ...channels },
          },
        })),
      seedUnreadChannels: (channelIds) =>
        set((s) => {
          const missing = channelIds.filter(
            (id) => id && s.unreadChannelMessages.channels[id] === undefined,
          );
          if (!missing.length) return s;
          return {
            unreadChannelMessages: {
              ...s.unreadChannelMessages,
              channels: {
                ...s.unreadChannelMessages.channels,
                ...Object.fromEntries(missing.map((id) => [id, 0])),
              },
            },
          };
        }),
      resetUnreadChannel: (channelId) =>
        set((s) => {
          if (s.unreadChannelMessages.channels[channelId] === undefined)
            return s;
          return {
            unreadChannelMessages: {
              ...s.unreadChannelMessages,
              channels: { ...s.unreadChannelMessages.channels, [channelId]: 0 },
            },
          };
        }),
      incrementUnreadChannel: (channelId) =>
        set((s) => {
          const current = s.unreadChannelMessages.channels[channelId];
          if (current === undefined) return s;
          return {
            unreadChannelMessages: {
              ...s.unreadChannelMessages,
              channels: {
                ...s.unreadChannelMessages.channels,
                [channelId]: (Number(current) || 0) + 1,
              },
            },
          };
        }),
      removeUnreadChannels: (channelIds) =>
        set((s) => {
          const present = channelIds.filter(
            (id) => s.unreadChannelMessages.channels[id] !== undefined,
          );
          if (!present.length) return s;
          const channels = { ...s.unreadChannelMessages.channels };
          present.forEach((id) => delete channels[id]);
          return {
            unreadChannelMessages: { ...s.unreadChannelMessages, channels },
          };
        }),
      setAllMembershipChannels: (allMembershipChannels) =>
        set({ allMembershipChannels }),
      setChatChannelsByConnection: (chatChannelsByConnection) =>
        set({ chatChannelsByConnection }),
    }),
    { name: "ChatStore" },
  ),
);

// Dev-only handle for inspecting unread badge state from the console — the
// unread map is keyed by PubNub channel name, which is the only place a badge
// count can be traced back to a ticket. Not shipped to production builds.
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__chatStore = useChatStore;
}
